import { makeAction, makeReducer } from '@finn-no/redux-actions';
import type { FilterType, ResetCurrentFilterPayload } from '@/components/filters/currentFilters/filterToFilterData';
import type { CheckboxFilters, CheckboxMap, Filters, Month, NumberRange } from '@/interfaces';
import defaultState from './defaultState';

const toggleCheckbox = (checkboxes: CheckboxMap, value: string): CheckboxMap => {
    const { [value]: toggledOn, ...rest } = checkboxes;
    return toggledOn ? rest : { ...rest, [value]: true };
};

const toggleMonth = (months: Month[], month: number, year: number): Month[] => {
    const copy = [...months];
    const index = copy.findIndex((e: Month) => e.month === month && e.year === year);
    if (index >= 0) {
        copy.splice(index, 1);
    } else {
        copy.push({ month, year });
    }
    return copy;
};

export const actions = {
    filter: makeAction<Filters>('SET_FILTERS', (state, action) => ({ ...state, ...action.payload })),
    resetFilters: makeAction<Filters>('RESET_FILTERS', () => ({ ...defaultState.filters })),
    resetHotelFilters: makeAction<Filters>('RESET_HOTEL_FILTERS', (state) => ({
        ...defaultState.filters,
        hotelId: state.hotelId,
    })),
    changeSort: makeAction<Filters>('CHANGE_SORT', (state, action) => ({ ...state, sort: action.payload, page: undefined })),
    changePage: makeAction<Filters>('CHANGE_PAGE', (state, action) => ({ ...state, page: action.payload })),
    changeOrigin: makeAction<Filters>('CHANGE_ORIGIN', (state, action) => ({ ...state, origin: action.payload, page: undefined })),
    toggleTravelMonth: makeAction<Filters>('TOGGLE_TRAVEL_MONTH', (state, action) => ({
        ...state,
        travelMonths: toggleMonth(state.travelMonths, action.payload.month, action.payload.year),
        page: undefined,
    })),
    changePaxCombination: makeAction<Filters>('CHANGE_PAX_COMBINATION', (state, action) => ({
        ...state,
        numberOfAdults: action.payload.numberOfAdults,
        agesOfChildren: action.payload.agesOfChildren,
        page: undefined,
    })),
    resetTravelMonths: makeAction<Filters>('RESET_TRAVEL_MONTHS', (state) => ({ ...state, travelMonths: [], page: undefined })),
    setDates: makeAction<Filters>('CHANGE_DEPARTURE_AND_RETURN_DATES', (state, { payload: newDates }) => ({
        ...state,
        departureDate: { ...state.departureDate, date: newDates.startDate },
        returnDate: { ...state.returnDate, date: newDates.endDate },
        page: undefined,
    })),
    changeDepartureDate: makeAction<Filters>('CHANGE_DEPARTURE_DATE', (state, action) => ({
        ...state,
        departureDate: { ...state.departureDate, date: action.payload },
        page: undefined,
    })),
    changeReturnDate: makeAction<Filters>('CHANGE_RETURN_DATE', (state, action) => ({
        ...state,
        returnDate: { ...state.returnDate, date: action.payload },
        page: undefined,
    })),
    changeFlexibleDeparture: makeAction<Filters>('CHANGE_FLEXIBLE_DEPARTURE', (state, action) => ({
        ...state,
        departureDate: { ...state.departureDate, flexibility: action.payload },
        page: undefined,
    })),
    changeFlexibleReturn: makeAction<Filters>('CHANGE_FLEXIBLE_RETURN', (state, action) => ({
        ...state,
        returnDate: { ...state.returnDate, flexibility: action.payload },
        page: undefined,
    })),
    changeDuration: makeAction<Filters>('CHANGE_DURATION', (state, action) => ({ ...state, duration: action.payload, page: undefined })),
    setIgnoredFilters: makeAction<Filters>('SET_IGNORED_FILTERS', (state, action) => ({
        ...state,
        ignoredFilters: action.payload,
        page: undefined,
    })),
    filterResetSpecific: makeAction<Filters>('FILTER_RESET_SPECIFIC', (state, action) => {
        const payload: ResetCurrentFilterPayload = action.payload;

        const filterChange: Record<FilterType, () => Partial<Filters>> = {
            checkboxMap: () => ({
                [payload.location]: toggleCheckbox(state[payload.location as CheckboxFilters], action.payload.value),
            }),
            dateSpecific: () => ({ departureDate: defaultState.filters.departureDate, returnDate: defaultState.filters.returnDate }),
            dateMonth: () => ({ travelMonths: defaultState.filters.travelMonths, duration: defaultState.filters.duration }),
            simple: () => ({ [payload.location]: defaultState.filters[payload.location as keyof Filters] }),
            boundingBox: () => ({ boundingBox: undefined, destinationLabel: undefined }),
        };

        return {
            ...state,
            ...filterChange[payload.filterType](),
            page: undefined,
        };
    }),
    filterCheckboxMeal: makeAction<Filters>('FILTER_CHECKBOX_MEAL', (state, action) => ({
        ...state,
        meals: toggleCheckbox(state.meals, action.payload),
        page: undefined,
    })),
    filterCheckboxFacility: makeAction<Filters>('FILTER_CHECKBOX_FACILITY', (state, action) => ({
        ...state,
        facilities: toggleCheckbox(state.facilities, action.payload),
        page: undefined,
    })),
    filterCheckboxActivity: makeAction<Filters>('FILTER_CHECKBOX_ACTIVITY', (state, action) => ({
        ...state,
        activities: toggleCheckbox(state.activities, action.payload),
        page: undefined,
    })),
    filterCheckboxSupplier: makeAction<Filters>('FILTER_CHECKBOX_SUPPLIER', (state, action) => ({
        ...state,
        suppliers: toggleCheckbox(state.suppliers, action.payload),
        page: undefined,
    })),
    filterCheckboxRating: makeAction<Filters>('FILTER_CHECKBOX_RATING', (state, action) => ({
        ...state,
        rating: toggleCheckbox(state.rating, action.payload),
        page: undefined,
    })),
    filterPriceRange: makeAction<Filters>('FILTER_PRICE_RANGE', (state, action) => ({
        ...state,
        priceRange: action.payload,
        page: undefined,
    })),
    filterDistanceToBeach: makeAction<Filters>('FILTER_DISTANCE_TO_BEACH', (state, action) => ({
        ...state,
        distanceToBeach: action.payload ? ([0, action.payload] as NumberRange) : undefined,
        page: undefined,
    })),
    filterDistanceToCity: makeAction<Filters>('FILTER_DISTANCE_TO_CITY', (state, action) => ({
        ...state,
        distanceToCity: action.payload ? ([0, action.payload] as NumberRange) : undefined,
        page: undefined,
    })),
    filterByKeywords: makeAction<Filters>('FILTER_BY_KEYWORDS', (state, action) => ({
        ...state,
        keywords: action.payload,
        page: undefined,
    })),
    setBoundingBox: makeAction<Filters>('SET_BOUNDING_BOX', (state, action) => ({
        ...state,
        boundingBox: action.payload,
        page: undefined,
    })),
    setDestinationLabel: makeAction<Filters>('SET_DESTINATION_LABEL', (state, action) => ({
        ...state,
        destinationLabel: action.payload,
    })),
    clearGeoFilters: makeAction<Filters>('CLEAR_GEO_FILTERS', (state) => ({
        ...state,
        boundingBox: undefined,
        destinationLabel: undefined,
        page: undefined,
    })),
};

export default actions;
export const filterReducer = makeReducer<Filters>(actions);
